import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Switch } from '@/components/ui/switch';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import {
  DealItem,
  DealItemWithVersions,
  ProductType,
} from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';

interface ColumnProps {
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
}

const useColumnDefProductType = ({
  handleCellEdit,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const isOfferLocked = useIsOfferLocked();
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'productType';
  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'Physique/Digital',
      sortingFn: createSubRowSortingFn('productType', (row) => {
        const productType = row.activeVersion?.productType;
        if (productType === ProductType.Coupon) return 'digital';
        if (productType === ProductType.Object) return 'physical';
        return null;
      }),
      header: () => (
        <div
          className="flex items-center"
          onClick={() => setColumnSort(accessorKey)}
        >
          {t('kpi.product_type')} <SortIcon pointer="productType" />
        </div>
      ),
      cell: ({ row }) => {
        const isParent = row.subRows.length > 1;

        if (isParent) {
          // Check if all subrows have the same product type
          const firstProductType =
            row.subRows[0]?.original.activeVersion.productType;
          const allSame = row.subRows.every(
            (subRow) =>
              subRow.original.activeVersion.productType === firstProductType
          );

          if (allSame && firstProductType) {
            const isCoupon = firstProductType === ProductType.Coupon;
            return (
              <div
                className={cn(
                  'h-full w-full',
                  (isParent || isOfferLocked) && 'opacity-50',
                  isParent || !isOfferLocked
                    ? 'cursor-pointer'
                    : 'cursor-not-allowed'
                )}
              >
                <div className="flex h-full items-center justify-center gap-2 px-2">
                  <span className="text-xs text-gray-500">
                    {t('general.physical')}
                  </span>
                  <Switch checked={isCoupon} disabled={true} />
                  <span className="text-xs text-gray-500">
                    {t('general.digital')}
                  </span>
                </div>
              </div>
            );
          }

          return (
            <div
              className={cn(
                'h-full w-full',
                (isParent || isOfferLocked) && 'opacity-50',
                isParent || !isOfferLocked
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed'
              )}
            >
              <div className="flex h-full items-center px-2">—</div>
            </div>
          );
        }

        const isCoupon =
          row.original.activeVersion.productType === ProductType.Coupon;

        return (
          <div className="h-full w-full">
            <div className="flex h-full items-center justify-center gap-2 px-2">
              <span className="text-xs text-gray-500">
                {t('general.physical')}
              </span>
              <Switch
                checked={isCoupon}
                disabled={isOfferLocked}
                onCheckedChange={async (checked) => {
                  await handleCellEdit(
                    row.original.activeVersion.id,
                    'productType',
                    (checked
                      ? ProductType.Coupon
                      : ProductType.Object) as unknown as DealItem['productType'],
                    !isParent
                  );
                }}
              />
              <span className="text-xs text-gray-500">
                {t('general.digital')}
              </span>
            </div>
          </div>
        );
      },
      size: 180,
    }),
    [t, isOfferLocked, setColumnSort, handleCellEdit]
  );
};

export default useColumnDefProductType;
